import { v4 as uuidV4 } from 'uuid';
import { defaultGTMDataLayerPayload, baseGtmAnalyticsConstants, GTM_EVENT_TYPES, GTM_EVENT_CATEGORY, GTM_EVENT_ACTION, RTE_TOOLBAR_DROPDOWN_ELEMENTS_LIST } from './constants';

/* eslint-disable no-console */

function gtmDataLayerHandler(payload) {
  if (!payload || typeof payload !== 'object') {
    console.warn('Invalid payload passed to dataLayer');
    return;
  }

  try {
    // Ensure dataLayer exists
    window.dataLayer = window.dataLayer || [];

    // Push payload
    window.dataLayer.push(payload);
  } catch (error) {
    console.error('Error pushing data to GTM dataLayer:', error);
  }
}

/*

* This common function is used to track the time stamp for the GTM event
* @returns {Object} - Object containing the timestamp in the format of ISO Format and Local Format and Unix Timestamp
* timestamp will be part of 'pageview' | 'user_interaction' | 'user_exception'
*/
function gtmTrackTimeStampHandler() {
  const now = new Date();

  // ISO format (UTC) -> e.g. 2026-04-15T12:34:56.789Z
  const transaction_dt = now.toISOString();

  // Local format -> YYYY-MM-DDThh:mm:ss.ms
  const pad = (num, size = 2) => String(num).padStart(size, '0');

  const transaction_local_dt =
    now.getFullYear() + '-' +
    pad(now.getMonth() + 1) + '-' +
    pad(now.getDate()) + 'T' +
    pad(now.getHours()) + ':' +
    pad(now.getMinutes()) + ':' +
    pad(now.getSeconds()) + '.' +
    pad(now.getMilliseconds(), 3);

  // Unix timestamp (in seconds)
  const event_timestamp = Math.floor(now.getTime() / 1000);

  return {
    event_timestamp, // 1744750530 | Unix Timestamp
    transaction_dt, //  "2026-04-15T10:25:30.123Z" | ISO Format
    transaction_local_dt, //  "2026-04-15T10:25:30.123" | Local Format
  };
}


function toCamelCase(str) {
  return str.replace(/_([a-z])/g, (_, char) => char.toUpperCase());
}

export function convertKeysToCamelCase(data) {
  if (Array.isArray(data)) {
    return data.map(convertKeysToCamelCase);
  }

  if (data !== null && typeof data === "object") {
    return Object.keys(data).reduce((acc, key) => {
      const camelKey = key.includes("_") ? toCamelCase(key) : key;
      acc[camelKey] = convertKeysToCamelCase(data[key]);
      return acc;
    }, {});
  }

  return data;
}

/** app_id, app_version, platform — included on global_user events. */
function appFields(payload = {}) {
  return {
    app_id: payload.app_id ?? baseGtmAnalyticsConstants.app_id,
    app_version: payload.app_version ?? baseGtmAnalyticsConstants.app_version,
    platform: payload.platform ?? baseGtmAnalyticsConstants.platform,
  };
}

function pageviewFields(payload = {}) {
  const {
    courseId = null,
    courseSectionId = null,
    sectionId = null,
    contentId = null,
    contentIdType = null,
    product_id: payloadProductId = null,
    product_platform_code: payloadProductPlatformCode = null,
    product_model_name: payloadProductModelName = null,
    business_model_code: payloadBusinessModelCode = null,
  } = payload;

  const {
    product_id,
    product_platform_code,
    product_model_name,
    business_model_code,
  } = defaultGTMDataLayerPayload;

  return {
    product_model_name: payloadProductModelName ?? product_model_name ?? null,
    product_id: payloadProductId ?? product_id ?? null,
    product_platform_code: payloadProductPlatformCode ?? product_platform_code ?? null,
    business_model_code: payloadBusinessModelCode ?? business_model_code ?? null,
    course_id: courseId ?? null,
    course_section_id: courseSectionId ?? sectionId ?? null,
    content_id: contentId ?? null,
    content_id_type: contentIdType ?? null,
  };
}

/** Courseware context fields for dataLayer (snake_case). */
function coursewareFields(payload = {}, { includeScreenId = true } = {}) {
  const {
    workspace_id = null,
    courseId = null,
    lessonId = null,
    chapterId = null,
    screenId = null,
    sectionId = null,
  } = payload;

  return {
    workspace_id,
    ...(courseId !== null && courseId !== undefined ? { course_id: courseId } : {}),
    ...(lessonId !== null && lessonId !== undefined ? { lesson_id: lessonId } : {}),
    ...(chapterId !== null && chapterId !== undefined ? { chapter_id: chapterId } : {}),
    ...(includeScreenId && screenId !== null && screenId !== undefined ? { screen_id: screenId } : {}),
    ...(sectionId !== null && sectionId !== undefined ? { section_id: sectionId } : {}),
  };
}

export function gtmTrackPageView(payload) {
  try {
    const { pageName = null } = payload;
    const pageviewObj = {
      event: GTM_EVENT_TYPES.PAGEVIEW,
      page_title: pageName ?? null,
      ...pageviewFields(payload),
      message_id: uuidV4(),
      ...gtmTrackTimeStampHandler(),
    };
    gtmDataLayerHandler(pageviewObj);
  } catch (error) {
    console.error('Error tracking page view:', error);
  }
}

export function gtmTrackGlobalLaunchEventHandler(payload) {
  try {
    const globalLaunchObj = {
      event: 'global_user',
      ...appFields(payload),
    };
    gtmDataLayerHandler(globalLaunchObj); // Push the payload to the GTM dataLayer
  } catch (error) {
    console.error('Error GTM tracking global launch:', error);
  }
}

export function gtmTrackGlobalObligatoryLaunchEventHandler(payload) {
  try {
    const {
      personIdType = null,
      event_action,
      personId = null,
      personRoleCode = null,
      sessionId = null,
    } = payload;

    const {
      secondary_person_id,
      secondary_person_id_type,
      secondary_person_role_code,
    } = defaultGTMDataLayerPayload;

    const globalObligatoryLaunchObj = {
      event: 'global_user',
      ...appFields(payload),
      event_action,
      person_id: personId,
      person_id_type: personIdType,
      person_role_code: personRoleCode,
      secondary_person_id,
      secondary_person_id_type,
      secondary_person_role_code,
      login_session_id: sessionId,
      ...gtmTrackTimeStampHandler(), // Timestamp
    };

    // Push the payload to the GTM dataLayer
    gtmDataLayerHandler(globalObligatoryLaunchObj);
  } catch (error) {
    console.error('Error GTM tracking global obligatory launch:', error);
  }
}


export function gtmTrackUserInteractionActionEventHandler(payload = {}){
  try{
    const {
      eventCategory = null,
      eventAction = null,
      eventLabel = null,
      eventValue = null,
      componentId = null,
      productId = null,
      locationInApp,
      assetId,
      assetType,
      assetSource,
    } = payload;
    const telemetryPageActionEventObj = {
      event: GTM_EVENT_TYPES.USER_INTERACTION,
      ...coursewareFields(payload),
      ...(locationInApp !== undefined && locationInApp !== null ? { location_in_app: locationInApp } : {}),
      event_category: eventCategory,
      event_action: eventAction,
      event_label: eventLabel,
      event_value: eventValue,
      component_id: componentId,
      product_id: productId,
      ...(assetId !== undefined && assetId !== null ? { asset_id: assetId } : {}),
      ...(assetType !== undefined && assetType !== null ? { asset_type: assetType } : {}),
      ...(assetSource !== undefined && assetSource !== null ? { asset_source: assetSource } : {}),
      ...gtmTrackTimeStampHandler(), // Timestamp
    };
    gtmDataLayerHandler(telemetryPageActionEventObj);
  } catch (error) {
    console.error('Error GTM tracking user action:', error);
  }
}


export function gtmTrackUserExceptionEventHandler(payload){
  try{
    const { errorCode = null, errorUrl = null, errorMessage = null } = payload;
    const userExceptionObj = {
      event: GTM_EVENT_TYPES.USER_EXCEPTION,
      error_code: errorCode,
      error_url: errorUrl,
      error_message: errorMessage,
      ...coursewareFields(payload),
      ...gtmTrackTimeStampHandler(), // Timestamp
    };
    gtmDataLayerHandler(userExceptionObj); // Push the payload to the GTM dataLayer
  } catch (error) {
    console.error('Error GTM tracking user exception:', error);
  }
}

export function trackUserInteractionActionEventHandler(payload = {}){
  try{
    const {
      eventCategory = payload?.interactionType === 'Section Content' ? GTM_EVENT_CATEGORY.SECTION_CONTENT : null,
      eventAction = RTE_TOOLBAR_DROPDOWN_ELEMENTS_LIST.includes(payload?.actionLabel) ? GTM_EVENT_ACTION.SELECT_ELEMENT_TO_ADD_FROM_DROPDOWN : null,
      eventValue = null,
      componentId = null,
      productId = null,
      locationInApp,
      assetId = null,
      assetType = null,
      assetSource = null,
      eventLabel = null,
      actionType = null,
      actionLabel = null,
      commandSourceCode = null,
      targetUrl = null,
    } = payload;
    const telemetryPageActionEventObj = {
      event: GTM_EVENT_TYPES.USER_INTERACTION,
      ...coursewareFields(payload),
      ...(locationInApp !== undefined && locationInApp !== null ? { location_in_app: locationInApp } : {}),
      event_category: eventCategory,
      event_action: eventAction ?? actionType,
      event_label: eventLabel ?? actionLabel,
      event_value: eventValue,
      component_id: componentId,
      product_id: productId,
      asset_id: assetId,
      asset_type: assetType,
      asset_source: assetSource,
      ...(commandSourceCode !== undefined && commandSourceCode !== null ? { command_source_code: commandSourceCode } : {}),
      ...(targetUrl !== undefined && targetUrl !== null ? { target_url: targetUrl } : {}),
      ...gtmTrackTimeStampHandler(),
    };
    gtmDataLayerHandler(telemetryPageActionEventObj);
  } catch (error) {
    console.error('Error GTM tracking user action:', error);
  }
}